/**
 * Uppercase the first letter of every word — the JS equivalent of CSS
 * `text-transform: capitalize` (any letter preceded by the start of the string
 * or a non-letter is uppercased; the rest is left untouched). Used to display
 * the lowercase OAF unit/cadence (e.g. `user/month`) as `User/Month`. Applied
 * in TS rather than via `text-transform` because the pricing summary's
 * tablet/mobile inline variant embeds this string inside a full "Starting at
 * $X/…" headline, and we must capitalize only the cadence, not the "at" in
 * "Starting at". No-op on caseless scripts (e.g. the JP cadence labels), so it
 * is locale-safe.
 */
export const capitalizeWords = (value: string): string =>
    value.replace(/(^|[^\p{L}])(\p{L})/gu, (_, boundary, letter) => `${boundary}${letter.toUpperCase()}`);

export interface FrequencyLabels {
    monthly: string;
    yearly: string;
    'one-time': string;
}

export function buildFrequencyLabels(labels: {
    frequencyMonthly: string;
    frequencyYearly: string;
    frequencyOneTime: string;
}): FrequencyLabels {
    return {
        monthly: labels.frequencyMonthly,
        yearly: labels.frequencyYearly,
        'one-time': labels.frequencyOneTime,
    };
}

/**
 * Localized labels for the standard OAF plan `units` values. Only the fixed
 * enum members are translatable; the `custom` units case carries publisher
 * free-text (`custom_unit`) that is not ours to localize and falls back to its
 * raw value at the call site.
 */
export interface UnitLabels {
    user: string;
    company: string;
}

export function buildUnitLabels(labels: { unitUser: string; unitCompany: string }): UnitLabels {
    return {
        user: labels.unitUser,
        company: labels.unitCompany,
    };
}
